// =========================================================================
// HelpyDoc Metabolic · MobileScreens — Router de pantallas
//
// Línea visual ÚNICA: "modern". La línea clásica (piloto) se retiró el
// 2026-07-25 por decisión de producto (T8): Modern es la referencia. El
// código del piloto vive en el historial de git si hiciera falta consultarlo.
// =========================================================================
import { lazy, Suspense, useState } from 'react';
import type { Session } from '@/services/AuthService';
import type { ToastType } from '@/components';
import { SkeletonDashboard, ErrorBoundary, AdminGate } from '@/components';
import { esAdminInformesActivo } from '@/config/clinica';

// Lazy imports — cada página se carga solo cuando se navega a ella
const TodayPageModern      = lazy(() => import('@/pages/TodayPageModern').then(m => ({ default: m.TodayPageModern })));
const RegistrarComidaPageModern = lazy(() => import('@/pages/RegistrarComidaPageModern').then(m => ({ default: m.RegistrarComidaPageModern })));
const PerfilPageModern     = lazy(() => import('@/pages/PerfilPageModern').then(m => ({ default: m.PerfilPageModern })));
const ActividadFisicaPageModern = lazy(() => import('@/pages/ActividadFisicaPageModern').then(m => ({ default: m.ActividadFisicaPageModern })));
const EstadoEmocionalPageModern = lazy(() => import('@/pages/EstadoEmocionalPageModern').then(m => ({ default: m.EstadoEmocionalPageModern })));
const PlatosSaludablesPageModern = lazy(() => import('@/pages/PlatosSaludablesPageModern').then(m => ({ default: m.PlatosSaludablesPageModern })));
const NeveraPageModern     = lazy(() => import('@/pages/NeveraPageModern').then(m => ({ default: m.NeveraPageModern })));
const PlatoDetallePageModern = lazy(() => import('@/pages/PlatoDetallePageModern').then(m => ({ default: m.PlatoDetallePageModern })));
const InformesPageModern   = lazy(() => import('@/pages/InformesPageModern').then(m => ({ default: m.InformesPageModern })));
const ResumenSemanalPage   = lazy(() => import('@/pages/ResumenSemanalPage').then(m => ({ default: m.ResumenSemanalPage })));
const AjustesPageModern    = lazy(() => import('@/pages/AjustesPageModern').then(m => ({ default: m.AjustesPageModern })));
const LogrosPageModern     = lazy(() => import('@/pages/LogrosPageModern').then(m => ({ default: m.LogrosPageModern })));
const DiarioPageModern     = lazy(() => import('@/pages/DiarioPageModern').then(m => ({ default: m.DiarioPageModern })));
const ComidaDetallePageModern = lazy(() => import('@/pages/ComidaDetallePageModern').then(m => ({ default: m.ComidaDetallePageModern })));
const MiConsentimientoPage = lazy(() => import('@/pages/MiConsentimientoPage').then(m => ({ default: m.MiConsentimientoPage })));
const BibliotecaPageModern = lazy(() => import('@/pages/BibliotecaPageModern').then(m => ({ default: m.BibliotecaPageModern })));
// HD-045 · Asistente automático del programa (chatbot RAG), gateado por
// VITE_ASISTENTE (ver AjustesPageModern) — `lazy` como el resto: quien no use
// el flag no descarga ni su código ni sus dependencias.
const AsistentePageModern = lazy(() => import('@/pages/AsistentePageModern').then(m => ({ default: m.AsistentePageModern })));
// HD-042 · Salud conectada. `lazy` como el resto: quien no use la sincronización
// de salud no descarga ni su código ni el del plugin.
const SaludConectadaPage   = lazy(() => import('@/pages/SaludConectadaPage').then(m => ({ default: m.SaludConectadaPage })));
const ProgresoSaludPage    = lazy(() => import('@/pages/ProgresoSaludPage').then(m => ({ default: m.ProgresoSaludPage })));
// Panel de administración. `lazy` como el resto: un paciente nunca navega aquí,
// así que su código no entra en la descarga inicial de nadie.
const AdminPlatosPage      = lazy(() => import('@/pages/AdminPlatosPage').then(m => ({ default: m.AdminPlatosPage })));
const AdminConsejosPage    = lazy(() => import('@/pages/AdminConsejosPage').then(m => ({ default: m.AdminConsejosPage })));
const AdminInformesPage    = lazy(() => import('@/pages/AdminInformesPage').then(m => ({ default: m.AdminInformesPage })));
const AdminBajasPage       = lazy(() => import('@/pages/AdminBajasPage').then(m => ({ default: m.AdminBajasPage })));

export interface MobileScreensProps {
  screen: string;
  navDir: 'forward' | 'back';
  onNav: (id: string) => void;
  onLogout: () => void;
  darkMode: boolean;
  onToggleDark: () => void;
  refreshKey: number;
  onRefresh: () => void;
  showToast: (msg: string, type?: ToastType) => void;
  session: Session;
}

function PageFallback() {
  return <SkeletonDashboard />;
}

export function MobileScreens({
  screen, navDir, onNav, onLogout, darkMode, onToggleDark,
  refreshKey, onRefresh, showToast, session,
}: MobileScreensProps) {
  const [platoSeleccionado, setPlatoSeleccionado] = useState<string | null>(null);
  const [comidaSeleccionada, setComidaSeleccionada] = useState<string | null>(null);

  let content: React.ReactElement;

  const HomeScreen = <TodayPageModern onNav={onNav} refreshKey={refreshKey} onRefresh={onRefresh} showToast={showToast} onOpenComida={setComidaSeleccionada} />;
  const PerfilScreen = <PerfilPageModern onBack={() => onNav('home')} onLogout={onLogout} onNav={onNav} session={session} />;

  if (comidaSeleccionada) {
    content = <ComidaDetallePageModern id={comidaSeleccionada} onBack={() => setComidaSeleccionada(null)} onDeleted={onRefresh} />;
  } else if (screen === 'platos' && platoSeleccionado) {
    content = <PlatoDetallePageModern id={platoSeleccionado} onBack={() => setPlatoSeleccionado(null)} />;
  } else {
    switch (screen) {
      case 'home':      content = HomeScreen; break;
      case 'registro':  content = <RegistrarComidaPageModern onBack={() => onNav('home')} showToast={showToast} />; break;
      case 'actividad': content = <ActividadFisicaPageModern onBack={() => onNav('home')} />; break;
      case 'emocional': content = <EstadoEmocionalPageModern onBack={() => onNav('home')} showToast={showToast} />; break;
      case 'platos':    content = <PlatosSaludablesPageModern onBack={() => onNav('home')} onOpenDetalle={(id) => setPlatoSeleccionado(id)} onOpenNevera={() => onNav('nevera')} />; break;
      case 'nevera':    content = <NeveraPageModern onBack={() => onNav('platos')} showToast={showToast} />; break;
      case 'informes':  content = <InformesPageModern onBack={() => onNav('home')} onNav={onNav} />; break;
      case 'resumen-semanal':
        content = <ResumenSemanalPage onBack={() => onNav('informes')} />;
        break;
      case 'perfil':    content = PerfilScreen; break;
      case 'progreso':  content = PerfilScreen; break; // "Mi progreso" → Perfil (evolución de peso)
      case 'ajustes':   content = <AjustesPageModern onBack={() => onNav('home')} onNav={onNav} darkMode={darkMode} onToggleDark={onToggleDark} />; break;
      case 'privacidad': content = <MiConsentimientoPage onBack={() => onNav('ajustes')} />; break;
      case 'biblioteca': content = <BibliotecaPageModern onBack={() => onNav('home')} showToast={showToast} />; break;
      case 'asistente': content = <AsistentePageModern onBack={() => onNav('home')} showToast={showToast} />; break;
      case 'salud-conectada':
        content = <SaludConectadaPage onBack={() => onNav('ajustes')} onNav={onNav} showToast={showToast} userId={session.userId} />;
        break;
      case 'progreso-salud':
        content = <ProgresoSaludPage onBack={() => onNav('informes')} showToast={showToast} />;
        break;
      case 'logros':    content = <LogrosPageModern onBack={() => onNav('home')} />; break;
      case 'diario':    content = <DiarioPageModern onBack={() => { onNav('home'); onRefresh(); }} onOpenComida={setComidaSeleccionada} />; break;
      // Administración. El guard va en `AdminGate`, no aquí: si alguien escribe
      // la pantalla a mano sin ser admin, ve el aviso de acceso restringido.
      case 'admin-platos':
        content = <AdminGate onBack={() => onNav('ajustes')}>
          <AdminPlatosPage onBack={() => onNav('ajustes')} showToast={showToast} />
        </AdminGate>;
        break;
      // Consejos y apoyo van un nivel por encima que los platos: no son
      // contenido editorial sino umbrales clínicos y teléfonos de crisis, así
      // que los edita la dirección médica (`superadmin`), no una cuenta de
      // contenidos. Quitar `soloSuperadmin` los abriría al nivel `admin`.
      case 'admin-consejos':
        content = <AdminGate onBack={() => onNav('ajustes')} soloSuperadmin>
          <AdminConsejosPage onBack={() => onNav('ajustes')} showToast={showToast} />
        </AdminGate>;
        break;
      // HD-043 · Informes de pacientes. Doble candado, y los dos hacen falta:
      // `soloSuperadmin` porque se entra en la historia de un tercero, y el flag
      // porque falta la revisión legal del consentimiento. Con el flag apagado
      // la pantalla no existe ni para el superadmin — cae al `default`.
      // F03 · Solicitudes de baja (ADR-024). Nivel `admin` (no solo superadmin):
      // revisar una baja decide sobre la solicitud, no abre historia clínica.
      // El servidor exige lo mismo con `authorize('admin')`.
      case 'admin-bajas':
        content = <AdminGate onBack={() => onNav('ajustes')}>
          <AdminBajasPage onBack={() => onNav('ajustes')} showToast={showToast} />
        </AdminGate>;
        break;
      case 'admin-informes':
        if (!esAdminInformesActivo()) { content = HomeScreen; break; }
        content = <AdminGate onBack={() => onNav('ajustes')} soloSuperadmin>
          <AdminInformesPage onBack={() => onNav('ajustes')} showToast={showToast} />
        </AdminGate>;
        break;
      // Video Faktory · espejo de solo lectura del motor audiovisual. Nació como
      // página estática en `public/`, que la dejaba legible para cualquiera con
      // la URL: enseña estado interno, bloqueos y enlaces de trabajo, así que
      // vive aquí y hereda el candado. `soloSuperadmin` porque es información de
      // dirección, no de una cuenta de contenidos.
      default:          content = HomeScreen; break;
    }
  }

  // Usar key para forzar remount y dispara la animación CSS en cada cambio de pantalla
  // navDir determina la clase: screen-enter (→) o screen-enter-back (←)
  return (
    <Suspense fallback={<PageFallback />}>
      <ErrorBoundary key={screen}>
        <div className={navDir === 'back' ? 'screen-enter-back' : 'screen-enter'}>
          {content}
        </div>
      </ErrorBoundary>
    </Suspense>
  );
}
